import { ref, computed } from 'vue';

/**
 * Composable for managing multi-select state and operations
 * Handles individual item selection, select all, and selection tracking
 * 
 * @param {Object} options - Configuration options
 * @param {Array} options.items - Array of items (reactive ref)
 * @param {string} options.idKey - Key to use as item identifier (default: 'id')
 * @returns {Object} Selection state and methods
 */
export function useSelection(options = {}) {
  const {
    items = ref([]),
    idKey = 'id'
  } = options;

  const selectedItems = ref([]);
  const selectAll = ref(false);

  /**
   * Count of selected items
   */
  const selectedCount = computed(() => selectedItems.value.length);

  /**
   * Check if any items are selected
   */
  const hasSelection = computed(() => selectedCount.value > 0);

  /**
   * Check if all items are selected
   */
  const allSelected = computed(() => {
    if (!items.value || items.value.length === 0) return false;
    return selectedCount.value === items.value.length;
  });

  /**
   * Check if some (but not all) items are selected
   */
  const someSelected = computed(() => {
    return hasSelection.value && !allSelected.value;
  });

  /**
   * Check if a specific item is selected
   * @param {number|string} itemId - Item ID to check
   * @returns {boolean} Whether item is selected
   */
  const isSelected = (itemId) => {
    return selectedItems.value.includes(itemId);
  };

  /**
   * Toggle selection of a specific item
   * @param {number|string} itemId - Item ID to toggle
   */
  const toggleSelect = (itemId) => {
    const index = selectedItems.value.indexOf(itemId);
    
    if (index > -1) {
      // Remove from selection
      selectedItems.value.splice(index, 1);
    } else {
      // Add to selection
      selectedItems.value.push(itemId);
    }

    // Update selectAll checkbox state
    updateSelectAllState();
  };

  /**
   * Toggle selection of all items
   */
  const toggleSelectAll = () => {
    if (selectAll.value) {
      // Deselect all
      clearSelection();
    } else {
      // Select all
      selectAllItems();
    }
  };

  /**
   * Select all items
   */
  const selectAllItems = () => {
    if (!items.value) return;
    
    selectedItems.value = items.value.map(item => item[idKey]);
    selectAll.value = true;
  };

  /**
   * Clear all selections
   */
  const clearSelection = () => {
    selectedItems.value = [];
    selectAll.value = false;
  };

  /**
   * Select specific items by IDs
   * @param {Array} itemIds - Array of item IDs to select
   */
  const selectItems = (itemIds) => {
    selectedItems.value = [...itemIds];
    updateSelectAllState();
  };

  /**
   * Update selectAll checkbox state based on current selection
   */
  const updateSelectAllState = () => {
    if (!items.value || items.value.length === 0) {
      selectAll.value = false;
      return;
    }
    
    selectAll.value = selectedCount.value === items.value.length;
  };

  /**
   * Get array of selected item objects (not just IDs)
   * @returns {Array} Array of selected item objects
   */
  const getSelectedItems = () => {
    if (!items.value) return [];
    
    return items.value.filter(item => 
      selectedItems.value.includes(item[idKey])
    );
  };

  /**
   * Get array of unselected item IDs
   * @returns {Array} Array of unselected item IDs
   */
  const getUnselectedIds = () => {
    if (!items.value) return [];
    
    return items.value
      .filter(item => !selectedItems.value.includes(item[idKey]))
      .map(item => item[idKey]);
  };

  /**
   * Check if a specific number of items are selected
   * @param {number} count - Expected count
   * @returns {boolean} Whether exactly count items are selected
   */
  const hasSelectedCount = (count) => {
    return selectedCount.value === count;
  };

  return {
    // State
    selectedItems,
    selectAll,
    
    // Computed
    selectedCount,
    hasSelection,
    allSelected,
    someSelected,
    
    // Methods
    isSelected,
    toggleSelect,
    toggleSelectAll,
    selectAllItems,
    clearSelection,
    selectItems,
    updateSelectAllState,
    getSelectedItems,
    getUnselectedIds,
    hasSelectedCount
  };
}
